'use client';
import { useState, type ComponentProps } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from '../../lib/i18n';
import { elowenClient, apiErrorMessage } from '../../lib/elowenClient';
import type { BrainSessionInfo, ManagedSession } from '../../lib/types';
import { useToast } from './Toast';
import { Button } from './Button';
import { ConversationIcon } from './ConversationIcon';
import { IconPicker } from './IconPicker';
import { Popover, PopoverContent, PopoverTrigger } from './shadcn/popover';
import { Tooltip, TooltipAnchor, TooltipContent } from './shadcn/tooltip';
import { useTooltipAnchor } from './shadcn/useTooltipAnchor';

/** One editing control for every conversation surface; persists through the existing metadata route.
 *
 *  `size` is the shared button step the trigger stands on, so a row that keeps its whole action group
 *  compact asks for `icon-sm` through the size axis instead of restating a box in `className` (which is
 *  what left a 28px picker beside 36px neighbours). */
export function ConversationIconPicker({ session, size = 'icon', className = '', disabled = false }: {
  session: { id: string; title: string } & ComponentProps<typeof ConversationIcon>;
  /** The shared button size step; `icon` is the default 36px control, `icon-sm` the compact 32px one. */
  size?: ComponentProps<typeof Button>['size'];
  className?: string; disabled?: boolean;
}) {
  const { t } = useTranslation();
  const { toast } = useToast();
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const failure = session.programStatus?.state === 'error';
  const tip = useTooltipAnchor();
  const change = useMutation({
    mutationFn: (icon: Parameters<typeof elowenClient.brainSetSessionIcon>[1]) => elowenClient.brainSetSessionIcon(session.id, icon),
    onSuccess: result => {
      const update = <T extends { id: string; icon: string }>(rows: T[] | undefined) =>
        rows?.map(row => row.id === result.id ? { ...row, icon: result.icon } : row);
      qc.setQueryData<BrainSessionInfo[]>(['brain-sessions'], update);
      qc.setQueryData<ManagedSession[]>(['brain-managed-sessions'], update);
      void qc.invalidateQueries({ queryKey: ['brain-sessions'] });
      void qc.invalidateQueries({ queryKey: ['brain-managed-sessions'] });
      setOpen(false);
    },
    onError: error => toast(apiErrorMessage(error), 'error', { error }),
  });
  const trigger = <Button type="button" variant="ghost" size={size} disabled={disabled || change.isPending}
    aria-label={t.conversationIcon.change.replace('{title}', session.title || t.brainChat.untitled)}
    title={failure ? undefined : t.conversationIcon.title} className={`shrink-0 ${className}`}
    aria-describedby={failure && !open && tip.open ? tip.tooltipId : undefined}
    {...tip.hover} {...tip.focus} onClick={event => event.stopPropagation()}>
    <ConversationIcon {...session} />
  </Button>;
  return <Tooltip open={failure && !open && tip.open} onOpenChange={tip.onOpenChange}>
    <Popover open={open} onOpenChange={setOpen}>
    <PopoverTrigger asChild>
      {failure ? <TooltipAnchor asChild>{trigger}</TooltipAnchor> : trigger}
    </PopoverTrigger>
    <PopoverContent align="start" aria-label={t.conversationIcon.title}
      className="w-fit max-w-[calc(100vw-var(--spacing)*6)] p-2" onClick={event => event.stopPropagation()}>
      <p className="mb-2 px-1 text-small text-muted-foreground">{t.conversationIcon.title}</p>
      <IconPicker value={session.icon ?? ''} defaultIcon="MessageCircle" label={t.conversationIcon.title}
        autoFocus disabled={change.isPending} onChange={icon => change.mutate(icon === 'MessageCircle' ? '' : icon)} />
    </PopoverContent>
    </Popover>
    {failure ? <TooltipContent id={tip.tooltipId} side="right" className="max-w-[min(--spacing(64),calc(100vw-var(--spacing)*8))]">
      <span className="block text-emphasis text-destructive">{t.chat.activityFailed}</span>
      {session.activity?.detail.trim() ? <span className="mt-1 block break-words">{session.activity.detail}</span> : null}
    </TooltipContent> : null}
  </Tooltip>;
}
